import { useState } from "react";

import { getShowConfig } from "../../constants/Endpoints";
import { fileUpload } from "../../hooks/useImageUploader";
import { FileUploadCallbacks, FileUploadConfig } from "../../types/upload";
import { validateInputFile } from "../../utils/file";
import { configStore } from "../../utils/mobx/ConfigStore";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { envVarStore } from "../../utils/mobx/EnvVarStore";
import { staticLabelStore } from "../../utils/mobx/StaticLabelStore";
import { cn } from "../../utils/style";
import { DocumentArrowUp } from "../Icon/generated/DocumentArrowUp";

const FileSelectBox = ({
  isLogin,
  fileUploadConfig,
  fileUploadCallback,
  setErrorType,
  setErrorOn,
  setErrorCloseFunction,
  setErrorReloadFunction,
  imageUploadFileRef,
  t,
  endpointUrl,
}: {
  isLogin: boolean;
  fileUploadConfig: FileUploadConfig;
  fileUploadCallback: FileUploadCallbacks;
  setErrorType: (errorType: number) => void;
  setErrorOn: (errorOn: boolean) => void;
  setErrorCloseFunction: (errorCloseFunction: () => void) => void;
  setErrorReloadFunction: (errorReloadFunction: () => void) => void;
  imageUploadFileRef: React.RefObject<HTMLInputElement>;
  t: (key: string) => string;
  endpointUrl: string;
}) => {
  const [isDropping, setIsDroppinrg] = useState<boolean>(false);

  const { endpointType } = endpointStore;
  const { staticLabels } = staticLabelStore;
  const { multifileUpload } = envVarStore;

  // 드래그 중인 아이템이 영역 위에 있을 때 실행되는 함수
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDroppinrg(true);
  };

  // 드래그 영역 이탈시
  const onDragLeave = () => {
    setIsDroppinrg(false);
  };

  // drop시 실행되는 함수
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDroppinrg(false);
    if (!isLogin) {
      return;
    }

    const tempFile = event.dataTransfer.files;
    const files: File[] = [];

    for (const file of tempFile) {
      // 파일 유효성 검즘.
      if (!validateInputFile(file, setErrorType)) {
        setErrorOn(true);
        event.dataTransfer.clearData();
        setErrorCloseFunction(() => () => {
          document.getElementById("file-upload")?.click();
          setErrorOn(false);
        });
        setErrorReloadFunction(() => () => {
          setErrorOn(false);
        });

        return;
      }

      files.push(file);
    }

    fileUploadConfig.inputFiles = files;

    if (getShowConfig(endpointType)) {
      // 마지막으로 적용된 config 사용
      if (endpointType === "ocr") {
        fileUploadConfig.inferenceConfig = configStore.currentOcrConfig;
      } else if (endpointType === "layout-analysis") {
        fileUploadConfig.inferenceConfig = configStore.currentDPConfig;
      }
    }

    fileUpload(fileUploadConfig, fileUploadCallback);
  };

  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="subtle text-muted-darker">File</div>
      <div
        className={cn({
          "flex flex-col justify-center rounded-lg border border-dashed border-border-dark p-6 md:h-[50vh]":
            true,
          "border-accent bg-muted ring ring-accent-blue-background":
            isLogin && isDropping,
          "cursor-pointer": isLogin,
        })}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (isLogin) {
            imageUploadFileRef.current?.click();
          }
        }}
        onMouseEnter={onDragOver}
        onMouseLeave={onDragLeave}
      >
        <input
          id="file-upload"
          name="image"
          type="file"
          accept=".jpg,.jpeg,.png,.bmp,.pdf,.tif,.tiff,.heic,.heif,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.hwp"
          multiple={multifileUpload === "true"}
          onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
            const _file = e.target.files;

            fileUploadConfig.inputFiles = Array.from(_file!);

            await fileUpload(fileUploadConfig, fileUploadCallback);
          }}
          ref={imageUploadFileRef}
          style={{ display: "none" }}
        />
        <div className="flex flex-col items-center justify-center text-center">
          <div className="hidden md:flex">
            <DocumentArrowUp className="mx-auto w-full" aria-hidden="true" />
          </div>
          <div className="justify-center md:mt-3">
            {isLogin ? (
              <label className="flex cursor-pointer flex-row gap-1.5">
                <span className="md:p-ui-medium subtle-medium text-accent-blue">
                  {t(`demo_select.file_select`)}
                </span>

                <span className="md:p-ui-medium subtle-medium hidden text-foreground sm:flex">
                  {t(`demo_select.file_drop_here`)}
                </span>
              </label>
            ) : (
              <span className="p-ui-medium text-foreground">
                <a
                  href={`${document.referrer || process.env.REACT_APP_SAAS_URL || ""}/login?redirect=${endpointUrl}`}
                  target="_parent"
                  className="cursor-pointer text-accent-blue hover:text-accent-blue/80"
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {t("demo_select.login")}
                </a>
                {t("demo_select.to_upload_your_document")}
              </span>
            )}
          </div>
          <div className="mt-1.5 hidden gap-1 md:flex md:flex-col">
            {staticLabels.Common?.Message?.FirstPageWarning?.[1] && (
              <p className="mt-[4px] text-[13px] text-gray-400">
                {endpointType.toLowerCase() === "layout-analysis"
                  ? "The demo shows only the first three pages for multi-page files."
                  : staticLabels.Common.Message.FirstPageWarning[0]}
              </p>
            )}
            {staticLabels.Common?.Message?.FileSize?.[1] && (
              <p className="detail text-muted-foreground">
                {staticLabels.Common?.Message?.FileFormat?.[0]}{" "}
                {staticLabels.Common?.Message?.FileSize?.[0]}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileSelectBox;
